import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import PaymentOptionsTable from '../../pdv/components/PaymentOptionsTable.component';

import getApiLinkFeesService from '@giro/shared-services/pdv/getApiLinkFees.service';
import {
  PaymentLinkFee,
  simulatePaymentLink,
} from '../utils/paymentLinkSimulation.utils';

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

interface LinkFeeSimulationProps {
  amount: number;
  type: string;
}

export const LinkFeeSimulation = ({ amount, type }: LinkFeeSimulationProps) => {
  const [fees, setFees] = React.useState<PaymentLinkFee[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);
  const enabled = type === 'amount' && Number.isFinite(amount) && amount > 0;

  React.useEffect(() => {
    if (!enabled) return;
    let active = true;
    setLoading(true);
    setError(false);
    setFees([]);

    (async () => {
      try {
        const [success, result] = await getApiLinkFeesService();
        if (!active) return;
        if (!success || !Array.isArray(result)) {
          setError(true);
          return;
        }
        setFees(result);
      } catch {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [enabled, attempt]);

  const simulation = simulatePaymentLink(amount, fees);
  const rows = simulation.map((row, index) => ({
    key: `link_installment_${index}`,
    type: row.type === 'Pix' ? 'PIX' : row.type,
    installment:
      row.installments === 'Única' ? 'Única' : parseInt(row.installments, 10),
    installment_amount: currency.format(row.installmentAmount),
    amount: currency.format(row.totalAmount),
  }));
  const maxInstallments = Math.max(
    1,
    ...rows.map((row) => Number(row.installment) || 1)
  );

  return (
    <Paper
      component="section"
      aria-label="Simulador de taxas do link"
      sx={{ borderRadius: 2, p: { xs: 2, sm: 3 }, minWidth: 0 }}
    >
      {!enabled ? (
        <Alert severity="info">
          {type === 'amount'
            ? 'Informe o valor a cobrar para simular as formas de pagamento.'
            : 'A simulação está disponível para links por valor.'}
        </Alert>
      ) : loading ? (
        <Box display="flex" alignItems="center" gap={1.5} py={3} role="status">
          <CircularProgress size={22} />
          <Typography variant="body2">Consultando taxas do link…</Typography>
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              onClick={() => setAttempt((value) => value + 1)}
            >
              Tentar novamente
            </Button>
          }
        >
          Não foi possível consultar as taxas do link.
        </Alert>
      ) : rows.length === 0 ? (
        <Alert severity="info">
          Nenhuma taxa de link disponível para simular neste estabelecimento.
        </Alert>
      ) : (
        <PaymentOptionsTable
          rows={rows}
          maxInstallments={maxInstallments}
          ariaLabel="Simulação de taxas do link"
          responsive
        />
      )}
    </Paper>
  );
};
